import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import clientCandidateDataService from '../services/clientCandidateDataService';
import ColumnBasedLayout from './ColumnBasedLayout';
import ClientHeader from './ClientHeader';
import EnhancedCandidateHeader from './EnhancedCandidateHeader';
import NotesPanel from './NotesPanel';
import FloatingActionButtons from './FloatingActionButtons';

// Import client widget components
import ContactsLinksWidget from './widgets/client/ContactsLinksWidget';
import CandidateTrackingWidget from './widgets/client/CandidateTrackingWidget';
import CompensationWidget from './widgets/client/CompensationWidget';
import PreferencesWidget from './widgets/client/PreferencesWidget';
import CustomFieldsWidget from './widgets/client/CustomFieldsWidget';
import PositionsWidget from './widgets/client/PositionsWidget';
import EducationWidget from './widgets/client/EducationWidget';
import PipelinesWidget from './widgets/client/PipelinesWidget';
import ClientDocumentsWidget from './widgets/client/ClientDocumentsWidget';
import ActivityWidget from './widgets/client/ActivityWidget';

/**
 * ClientCandidateProfile Component
 * 
 * Client-side view of a candidate with their own data overlay
 * Features:
 * - Master header (ClientHeader) for navigation
 * - Enhanced candidate header with inline editable name, nickname, IDs
 * - Floating action buttons (Notes + Edit Layout)
 * - Column-based widget layout
 * - Resizable notes panel with mentions
 */

const ClientCandidateProfile = () => {
  const { candidateId } = useParams();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isNotesPanelOpen, setIsNotesPanelOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [editModeState, setEditModeState] = useState({});

  useEffect(() => {
    loadProfile();
  }, [candidateId, refreshTrigger]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const data = await clientCandidateDataService.getCompleteProfile(candidateId);
      setProfile(data);
    } catch (error) {
      console.error('Error loading profile:', error);
      alert('Failed to load candidate profile');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const handleEditModeChange = (state) => {
    setEditModeState(state);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading candidate profile...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-gray-600">Candidate not found</p>
          <button
            onClick={() => navigate('/client/archive')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Candidates
          </button>
        </div>
      </div>
    );
  }

  // Define widgets
  const widgets = {
    'contacts-links': ContactsLinksWidget,
    'candidate-tracking': CandidateTrackingWidget,
    'compensation': CompensationWidget,
    'preferences': PreferencesWidget,
    'custom-fields': CustomFieldsWidget,
    'positions': PositionsWidget,
    'education': EducationWidget,
    'pipelines': PipelinesWidget,
    'documents': ClientDocumentsWidget,
    'activity': ActivityWidget
  };

  // Props to pass to all widgets
  const widgetProps = {
    profile,
    candidateId,
    onRefresh: handleRefresh
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Master Client Header - Sticky at top */}
      <ClientHeader />

      {/* Enhanced Candidate Header - Name, Nickname, IDs */}
      <EnhancedCandidateHeader 
        profile={profile} 
        onRefresh={handleRefresh} 
      />

      {/* Floating Action Buttons - Notes + Edit Layout */}
      <FloatingActionButtons
        onEditLayout={() => editModeState.setIsEditMode?.(true)}
        onOpenNotes={() => setIsNotesPanelOpen(true)}
        isEditMode={editModeState.isEditMode}
        hasUnsavedChanges={editModeState.hasChanges}
        hiddenWidgetsCount={editModeState.hiddenWidgetsCount || 0}
        onSave={editModeState.onSave}
        onCancel={editModeState.onCancel}
        onReset={editModeState.onReset}
        isSaving={editModeState.isSaving}
      />

      {/* Column-Based Layout */}
      <div className="px-4 py-3">
        <ColumnBasedLayout
          pageType="client_candidate_profile"
          widgets={widgets}
          widgetProps={widgetProps}
          onEditModeChange={handleEditModeChange}
        />
      </div>

      {/* Notes Panel - No backdrop */}
      <NotesPanel
        isOpen={isNotesPanelOpen}
        onClose={() => setIsNotesPanelOpen(false)}
        candidateId={candidateId}
        candidateName={profile.name}
        firmId={profile.client_data?.firm_id}
        currentUser={JSON.parse(localStorage.getItem('user') || '{}')}
      />
    </div>
  );
};

export default ClientCandidateProfile;
